/**
 * CouponInput - Promo/coupon code input with expand/collapse behavior
 *
 * Three states:
 * - Collapsed: Shows "Have a promo code?" link
 * - Expanded: Shows the text input field
 * - Applied: Renders nothing (badge displayed in PricingHeader)
 */

import { FormField } from './FormField';

interface CouponInputProps {
  /** Current coupon value */
  value: string;
  /** Callback when coupon changes */
  onChange: (value: string) => void;
  /** Callback when coupon should be validated (on blur) */
  onValidate?: () => void;
  /** Error message from coupon validation */
  error?: string | null;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is expanded (visible) */
  isExpanded?: boolean;
  /** Callback to expand the input */
  onExpand?: () => void;
  /** Whether a coupon has been applied (badge shown in PricingHeader) */
  isApplied?: boolean;
}

export function CouponInput({
  value,
  onChange,
  onValidate,
  error,
  disabled,
  isExpanded = true,
  onExpand,
  isApplied = false,
}: CouponInputProps) {
  // When coupon is applied, badge is shown in PricingHeader - render nothing here
  if (isApplied) {
    return null;
  }

  // Collapsed state: show expandable link
  if (!isExpanded) {
    return (
      <div>
        <button
          type="button"
          onClick={onExpand}
          className="text-xs text-slate-500 underline hover:text-slate-700"
        >
          Have a promo code?
        </button>
      </div>
    );
  }

  // Expanded state: show the input field
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleBlur = () => {
    if (value && onValidate) {
      onValidate();
    }
  };

  return (
    <FormField
      label="Promo Code"
      name="coupon"
      type="text"
      placeholder="Enter promo code"
      autoComplete="off"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error || undefined}
      disabled={disabled}
    />
  );
}

export default CouponInput;
